/**
 * Op Codes for Ponder Flight Game Match Messages
 * Defines the operation codes used for client-server communication
 * 
 * IMPORTANT: These must match the server-side constants in:
 * game-server/modules/ponder-flight-game/op-codes.go
 * 
 * Op Code Ranges:
 *   - 0-99: Reserved for general system/Nakama/common services
 *   - 100+: Game-specific op codes
 * 
 * SUCCESS MESSAGE PATTERN:
 *   All broadcast success messages (operations that modify game state) include a 
 *   "sender_user_id" field containing the user_id of the player who initiated the action.
 *   This enables features like activity logs to correctly identify who performed each action.
 *   
 *   Example: { type: "unlock_city_success", city_id: 42, team: {...}, sender_user_id: "uuid" }
 */

// Payload interfaces are reused from the domain TYPES sections. These are
// type-only imports (erased at compile time), so the runtime module graph
// stays acyclic even though domains import MessageType from this file;
// eslint.config.js refuses a value import from domains/ here.
import type { Airport } from '../domains/airports';
import type { Fleet, AvailablePlanes, FleetRefusal } from '../domains/fleets';
import type { InfluenceStateMessage, Team } from '../domains/teams';
import type { Segment, SegmentRefusal } from '../domains/segments';
import type { Itinerary, RoutePricingPolicyView } from '../domains/itineraries';
import type { OnlinePlayerData } from '../domains/players';
import type { CapitalStateMessage, TeamCapital, DebtQuote, DebtExecution, EquityQuote, EquityExecution } from '../domains/capital';
import type { AnnualReportMessage, CFOStatementsMessage, QuarterlyReportMessage, TTMMetricsMessage } from '../domains/financials-quarterly';
import type { StrengthReportMessage } from '../domains/strength';
import type { OrderRefusal } from '../domains/order-block';
import type { CommercialSnapshot } from '../domains/commercial';
import type { Mission, TeamMissionStore } from '../domains/missions';
import type { RouteShareReport } from '../domains/route-share';
import type { PrivateChatEndMessage, PrivateChatStartMessage } from '../domains/private-voice-chat';
import type { SessionEndedMessage } from '../domains/session-ended';
import type { PlayerBannedMessage } from '../domains/ban-notification';
import type { AirplaneModel } from '../data/airplanes';

/**
 * Match Data Op Codes
 * 
 * === SYSTEM/COMMON OP CODES (0-99) ===
 * - OpCodePlayerJoined (1): Sent when a player joins the match
 * - OpCodePlayersList (2): Sent to newly joined player with complete list and teams
 * - OpCodePlayerLeft (3): Sent when a player has fully left (grace period expired)
 * - OpCodePlayerBanned (8): Sent when a player is banned from the match
 * - OpCodePlayerDisconnected (11): Sent when a player disconnects (still in grace period)
 * - OpCodeSessionEnded (9): Sent to all players when a scheduled session ends
 * 
 * === GAME-SPECIFIC OP CODES (100+) ===
 * - OpCodeAirportsState (100): Sent to newly joined player with all airports state
 * 
 * === FLEET MANAGEMENT OP CODES (104-119) ===
 * - OpCodeFleetsState (104): Sent to newly joined player with all fleets
 * - OpCodeAddFleetSlot (108): Sent by client to add a fleet configuration
 * - OpCodeAddFleetSlotSuccess (109): Broadcast when a fleet slot is added
 * - OpCodeAddFleetSlotFailed (110): Sent when adding a fleet slot fails
 * - OpCodeRemoveFleetSlot (111): Sent by client to remove a fleet configuration
 * - OpCodeRemoveFleetSlotSuccess (112): Broadcast when a fleet slot is removed
 * - OpCodeRemoveFleetSlotFailed (113): Sent when removing a fleet slot fails
 * - OpCodeUpdateFleetSlot (114): Sent by client to update a fleet configuration
 * - OpCodeUpdateFleetSlotSuccess (115): Broadcast when a fleet slot is updated
 * - OpCodeUpdateFleetSlotFailed (116): Sent when updating a fleet slot fails
 * - OpCodePurchaseAirplanes (117): Sent by client to purchase airplanes for a configuration
 * - OpCodePurchaseAirplanesSuccess (118): Broadcast when airplanes are purchased
 * - OpCodePurchaseAirplanesFailed (119): Sent when purchasing airplanes fails
 * 
 * === SEGMENT MANAGEMENT OP CODES (120-129) ===
 * - OpCodeSegmentsState (120): Sent to newly joined player with all segments
 * - OpCodeAddSegment (121): Sent by client to create a new segment
 * - OpCodeAddSegmentSuccess (122): Broadcast when a segment is created
 * - OpCodeAddSegmentFailed (123): Sent when creating a segment fails
 * - 124-129 retired: removing a whole route and changing its aircraft count
 *
 * === FLEET SALES OP CODES (130-132) ===
 * - OpCodeSellAirplanes (130): Sent by client to sell airplanes for a configuration
 * - OpCodeSellAirplanesSuccess (131): Broadcast when airplanes are sold
 * - OpCodeSellAirplanesFailed (132): Sent when selling airplanes fails
 *
 * === QUERY MAX FLIGHTS OP CODES (133-134) ===
 * - OpCodeQueryMaxFlights (133): Sent by client to query max flights per day for a route+plane
 * - OpCodeQueryMaxFlightsResult (134): Sent with calculated max flights per day
 *
 * === ITINERARY MANAGEMENT OP CODES (135-139) ===
 * - OpCodeUpdateItineraryPrice (135): Sent by client to change ticket price
 * - OpCodeUpdateItineraryPriceSuccess (136): Sent when price update succeeds
 * - OpCodeUpdateItineraryPriceFailed (137): Sent when price update fails
 * 
 * === AIRPORT FLIGHTS OP CODES (138-139) ===
 * - OpCodeSubscribeAirportFlights (138): Sent by client to subscribe to flights from/to an airport
 * - OpCodeAirportFlightsState (139): Sent to subscriber with all itineraries for an airport
 *
 * client_parity_test.go holds this table to the server's op codes
 * (op-codes.go): every name both declare has one value.
 */
export const OpCode = {
  OpCodeCommercialAction: 350,
  OpCodeCommercialState: 351,
  OpCodeCommercialActionFailed: 352,
  OpCodeInfluenceState: 353,
  OpCodePlayerJoined: 1,
  OpCodePlayersList: 2,
  OpCodePlayerLeft: 3,
  OpCodeLiveKitToken: 4,
  OpCodePrivateChatStart: 5,
  OpCodePrivateChatEnd: 6,
  OpCodePrivateChatAccepted: 7, // Sent by client when accepting private chat invitation
  OpCodePrivateChatLeft: 12, // Sent by client once out of a private call; the server answers with a fresh team token
  OpCodePlayerBanned: 8, // Sent by server when a player is banned
  OpCodePlayerDisconnected: 11, // Sent by server when a player disconnects (grace period)
  OpCodeSessionEnded: 9, // Sent by server to all players when a scheduled session ends
  OpCodePhaseChange: 10, // Sent when session lifecycle phase or gameplay sub-phase changes
  OpCodeAirportsState: 100,
  // 101-103 are reserved for the retired terminal-purchase flow.
  OpCodeFleetsState: 104,
  OpCodeAvailablePlanesState: 105,
  OpCodeAirplaneModelsState: 106,
  // 107 is reserved for the retired airplane-models update, which no server sent.
  OpCodeAddFleetSlot: 108,
  OpCodeAddFleetSlotSuccess: 109,
  OpCodeAddFleetSlotFailed: 110,
  OpCodeRemoveFleetSlot: 111,
  OpCodeRemoveFleetSlotSuccess: 112,
  OpCodeRemoveFleetSlotFailed: 113,
  OpCodeUpdateFleetSlot: 114,
  OpCodeUpdateFleetSlotSuccess: 115,
  OpCodeUpdateFleetSlotFailed: 116,
  OpCodePurchaseAirplanes: 117,
  OpCodePurchaseAirplanesSuccess: 118,
  OpCodePurchaseAirplanesFailed: 119,
  OpCodeSellAirplanes: 130,
  OpCodeSellAirplanesSuccess: 131,
  OpCodeSellAirplanesFailed: 132,
  // Query max flights per day op codes
  OpCodeQueryMaxFlights: 133,
  OpCodeQueryMaxFlightsResult: 134,
  // Segment management op codes
  OpCodeSegmentsState: 120,
  OpCodeAddSegment: 121,
  OpCodeAddSegmentSuccess: 122,
  OpCodeAddSegmentFailed: 123,
  // 124-129 retired: removing a whole route and changing its aircraft count,
  // which a flight's removal (225) and Add flight (121) replaced
  // Itinerary management op codes
  OpCodeUpdateItineraryPrice: 135,
  OpCodeUpdateItineraryPriceSuccess: 136,
  OpCodeUpdateItineraryPriceFailed: 137,
  // Airport flights op codes
  OpCodeSubscribeAirportFlights: 138,
  OpCodeAirportFlightsState: 139,
  OpCodeUpdateRoutePricingPolicy: 172,
  OpCodeUpdateRoutePricingPolicySuccess: 173,
  OpCodeUpdateRoutePricingPolicyFailed: 174,
  OpCodeResetItineraryPriceToAuto: 184,
  OpCodeResetItineraryPriceToAutoSuccess: 188,
  OpCodeResetItineraryPriceToAutoFailed: 189,
  // Team segments subscription op codes
  OpCodeTeamSegmentsState: 220,
  OpCodeTeamSegmentsUpdate: 221,
  // Flight instance op codes
  OpCodeUpdateFlightFrequency: 222,
  OpCodeUpdateFlightFrequencySuccess: 223,
  OpCodeUpdateFlightFrequencyFailed: 224,
  OpCodeRemoveFlight: 225,
  OpCodeRemoveFlightSuccess: 226,
  OpCodeRemoveFlightFailed: 227,
  // Financial op codes
  OpCodeFinancialReport: 144,
  OpCodeTimerSync: 145,
  OpCodeQuarterlyReport: 146,
  OpCodeAnnualReport: 147,
  OpCodeStrengthReport: 148,
  OpCodeCapitalState: 149,
  // 150-152 retired: one flights a day for every flight on a route, which each
  // flight's own (222) replaced
  // Airport flights subscription op codes
  OpCodeUnsubscribeAirportFlights: 153,
  OpCodeAirportFlightUpdate: 154,
  OpCodeAirportFlightRemoved: 155,
  // 156 retired: a client asking for the reports' history, which every join sends
  OpCodeCFOStatements: 157,
  OpCodeTTMMetrics: 158,
  OpCodeReportsHistory: 159, // The reports' history a join is sent, in batches (reports.go, SendReportsHistory)
  // 181-183 retired: upgrading a runway, which a city's investments replaced
  // Innovation op codes
  OpCodeUnlockInnovation: 185,
  OpCodeUnlockInnovationSuccess: 186,
  OpCodeUnlockInnovationFailed: 187,
  // Mission op codes
  OpCodeMissionsState: 190,
  // 191-193 retired: mission selection
  OpCodeMissionProgress: 194,
  OpCodeMissionCompleted: 195,
  // 196-202 retired: withdraw, pool, and redeem
  // Policy toggle op codes
  OpCodeTogglePolicy: 205,
  OpCodeTogglePolicySuccess: 206,
  OpCodeTogglePolicyFailed: 207,
  OpCodePolicyStatesSync: 208,
  // Emoji op codes
  OpCodeSendEmoji: 210,
  OpCodeEmojiReceived: 211,
  // Ping op codes
  OpCodeSendPing: 212,
  OpCodePingReceived: 213,
  // 215-219 retired: boardroom presence, which section presence replaced
  // OpCodes 230-232 are reserved (retired demographic market share system)
  // Route share op codes (lightweight city panel market share)
  OpCodeRequestRouteShare: 233,
  OpCodeRouteShareReport: 234,
  OpCodeRouteShareFailed: 235,
  // Capital op codes
  OpCodeCapitalDebtQuote: 160,
  OpCodeCapitalDebtQuoteSuccess: 161,
  OpCodeCapitalDebtQuoteFailed: 162,
  OpCodeCapitalDebtOrder: 163,
  OpCodeCapitalDebtOrderSuccess: 164,
  OpCodeCapitalDebtOrderFailed: 165,
  OpCodeCapitalEquityQuote: 166,
  OpCodeCapitalEquityQuoteSuccess: 167,
  OpCodeCapitalEquityQuoteFailed: 168,
  OpCodeCapitalEquityOrder: 169,
  OpCodeCapitalEquityOrderSuccess: 170,
  OpCodeCapitalEquityOrderFailed: 171,
  // DevTools op codes (900+)
  OpCodeDevSaveStressLocal: 903,
  OpCodeDevSaveStressLocalSuccess: 904,
  OpCodeDevSaveStressLocalFailed: 905,
  // Admin cheat op codes (910+)
  OpCodeAdminAddResources: 910,
  OpCodeAdminAddResourcesSuccess: 911,
  OpCodeAdminAddResourcesFailed: 912,
  OpCodeAdminJumpToNextTick: 913,
  OpCodeAdminJumpToNextTickSuccess: 914,
  OpCodeAdminJumpToNextTickFailed: 915,
  OpCodeAdminSetInnovationSpendLocked: 916,
  OpCodeAdminSetInnovationSpendLockedSuccess: 917,
  OpCodeAdminSetInnovationSpendLockedFailed: 918,
  OpCodeAdminForceEndMatch: 919,
  OpCodeAdminForceEndMatchSuccess: 920,
  OpCodeAdminForceEndMatchFailed: 921,
  // Help Request op codes (240-249)
  OpCodeRequestHelp: 240,
  OpCodeRequestHelpSuccess: 241,
  OpCodeCancelHelp: 242,
  OpCodeCancelHelpSuccess: 243,
  OpCodeHelpRequestResolved: 244,
  // A refused help request, answered to the requester alone (help-request.go)
  OpCodeRequestHelpFailed: 245,
  // 270-289 intentionally unused
  // Section presence op codes (290-294)
  OpCodeSectionPresenceSubscribe: 290,
  OpCodeSectionPresenceState: 292,
  OpCodeSectionPresenceUpdate: 293,
  OpCodeSectionCursorPosition: 294,
  // Co-sign button hover op code (295)
  OpCodeButtonHover: 295,
  // Budget op codes (296-298)
  OpCodeBudgetState: 296,
  OpCodeBudgetUpdate: 297,
  OpCodeBudgetRefresh: 298,
  // Tick processing state op code (299)
  OpCodeTickProcessingState: 299,
  // City access op codes (300-303)
  OpCodeUnlockCity: 300,
  OpCodeUnlockCitySuccess: 301,
  OpCodeUnlockCityFailed: 302,
  OpCodeUnlockedCitiesState: 303,
  // Performance monitoring op codes (304-308)
  OpCodeWorldSaveStart: 304,
  OpCodeWorldSaveEnd: 305,
  OpCodeManifestTickStart: 306,
  OpCodeManifestTickEnd: 307,
  OpCodeWorldSavePrep: 308,
  // Session script op codes (320-331)
  OpCodeSessionScriptStep: 320,   // Server → client: current session script step info and features
  OpCodeVoiceChatControl: 321,    // Server → client: voice chat enabled/disabled by server
  OpCodeDialogueComplete: 322,    // Client → server: player finished reading a dialogue
  OpCodeGlobalAudioMute: 323,     // Server → client (on join): session has global audio muted
  OpCodeTeamActivity: 330,        // Server → client: full team activity state (phase transitions, timers)
  OpCodeTeamActivityAction: 331,  // Client → server: player action within an activity phase
  OpCodeAchievementAwarded: 340,  // Server → client: player earned an achievement
  // 345-348 retired: a collaborative city unlock request, which the team's hover
  // on a city's investment replaced
} as const;

export type OpCode = typeof OpCode[keyof typeof OpCode];

/**
 * Message Type Constants
 * These string values must match the "type" field in message payloads.
 * client_parity_test.go holds this table to the server's message types
 * (op-codes.go and the files beside it): every name both declare has one
 * value. A message's type is spelled here and nowhere else
 * (message-type-by-hand).
 */
export const MessageType = {
  MessageTypePlayerJoined: 'player_joined',
  MessageTypePlayersList: 'players_list',
  MessageTypePlayerLeft: 'player_left',
  MessageTypePlayerDisconnected: 'player_disconnected',
  MessageTypeLiveKitToken: 'livekit_token',
  MessageTypePrivateChatStart: 'private_chat_start',
  MessageTypePrivateChatEnd: 'private_chat_end',
  MessageTypePrivateChatAccepted: 'private_chat_accepted',
  MessageTypePrivateChatLeft: 'private_chat_left',
  MessageTypeSessionEnded: 'session_ended',
  MessageTypePlayerBanned: 'player_banned',
  MessageTypeAirportsState: 'airports_state',
  MessageTypeFleetsState: 'fleets_state',
  MessageTypeAvailablePlanesState: 'available_planes_state',
  MessageTypeAirplaneModelsState: 'airplane_models_state',
  MessageTypeAddFleetSlot: 'add_fleet_slot',
  MessageTypeAddFleetSlotSuccess: 'add_fleet_slot_success',
  MessageTypeAddFleetSlotFailed: 'add_fleet_slot_failed',
  MessageTypeRemoveFleetSlot: 'remove_fleet_slot',
  MessageTypeRemoveFleetSlotSuccess: 'remove_fleet_slot_success',
  MessageTypeRemoveFleetSlotFailed: 'remove_fleet_slot_failed',
  MessageTypeUpdateFleetSlot: 'update_fleet_slot',
  MessageTypeUpdateFleetSlotSuccess: 'update_fleet_slot_success',
  MessageTypeUpdateFleetSlotFailed: 'update_fleet_slot_failed',
  MessageTypePurchaseAirplanes: 'purchase_airplanes',
  MessageTypePurchaseAirplanesSuccess: 'purchase_airplanes_success',
  MessageTypePurchaseAirplanesFailed: 'purchase_airplanes_failed',
  MessageTypeSellAirplanes: 'sell_airplanes',
  MessageTypeSellAirplanesSuccess: 'sell_airplanes_success',
  MessageTypeSellAirplanesFailed: 'sell_airplanes_failed',
  // Query max flights per day message types
  MessageTypeQueryMaxFlights: 'query_max_flights',
  MessageTypeQueryMaxFlightsResult: 'query_max_flights_result',
  // Segment message types
  MessageTypeSegmentsState: 'segments_state',
  MessageTypeAddSegment: 'add_segment',
  MessageTypeAddSegmentSuccess: 'add_segment_success',
  MessageTypeAddSegmentFailed: 'add_segment_failed',
  MessageTypeUpdateFlightFrequency: 'update_flight_frequency',
  MessageTypeUpdateFlightFrequencySuccess: 'update_flight_frequency_success',
  MessageTypeUpdateFlightFrequencyFailed: 'update_flight_frequency_failed',
  MessageTypeRemoveFlight: 'remove_flight',
  MessageTypeRemoveFlightSuccess: 'remove_flight_success',
  MessageTypeRemoveFlightFailed: 'remove_flight_failed',
  // Itinerary message types
  MessageTypeUpdateItineraryPrice: 'update_itinerary_price',
  MessageTypeUpdateItineraryPriceSuccess: 'update_itinerary_price_success',
  MessageTypeUpdateItineraryPriceFailed: 'update_itinerary_price_failed',
  MessageTypeUpdateRoutePricingPolicy: 'update_route_pricing_policy',
  MessageTypeUpdateRoutePricingPolicySuccess: 'update_route_pricing_policy_success',
  MessageTypeUpdateRoutePricingPolicyFailed: 'update_route_pricing_policy_failed',
  MessageTypeResetItineraryPriceToAuto: 'reset_itinerary_price_to_auto',
  MessageTypeResetItineraryPriceToAutoSuccess: 'reset_itinerary_price_to_auto_success',
  MessageTypeResetItineraryPriceToAutoFailed: 'reset_itinerary_price_to_auto_failed',
  // Airport flights message types
  MessageTypeSubscribeAirportFlights: 'subscribe_airport_flights',
  MessageTypeUnsubscribeAirportFlights: 'unsubscribe_airport_flights',
  MessageTypeAirportFlightsState: 'airport_flights_state',
  MessageTypeAirportFlightUpdate: 'airport_flight_update',
  MessageTypeAirportFlightRemoved: 'airport_flight_removed',
  // Team segments subscription message types
  MessageTypeTeamSegmentsState: 'team_segments_state',
  MessageTypeTeamSegmentsUpdate: 'team_segments_update',
  // Financial message types
  MessageTypeFinancialReport: 'financial_report',
  MessageTypeTimerSync: 'timer_sync',
  MessageTypeQuarterlyReport: 'quarterly_report',
  MessageTypeAnnualReport: 'annual_report',
  MessageTypeStrengthReport: 'strength_report',
  MessageTypeCapitalState: 'capital_state',
  MessageTypeCFOStatements: 'cfo_statements',
  MessageTypeTTMMetrics: 'ttm_metrics',
  MessageTypeReportsHistory: 'reports_history',
  // Innovation message types
  MessageTypeUnlockInnovation: 'unlock_innovation',
  MessageTypeUnlockInnovationSuccess: 'unlock_innovation_success',
  MessageTypeUnlockInnovationFailed: 'unlock_innovation_failed',
  // Mission message types
  MessageTypeMissionsState: 'missions_state',
  MessageTypeMissionProgress: 'mission_progress',
  MessageTypeMissionCompleted: 'mission_completed',
  // Capital message types
  MessageTypeCapitalDebtQuote: 'capital_debt_quote',
  MessageTypeCapitalDebtQuoteSuccess: 'capital_debt_quote_success',
  MessageTypeCapitalDebtQuoteFailed: 'capital_debt_quote_failed',
  MessageTypeCapitalDebtOrder: 'capital_debt_order',
  MessageTypeCapitalDebtOrderSuccess: 'capital_debt_order_success',
  MessageTypeCapitalDebtOrderFailed: 'capital_debt_order_failed',
  MessageTypeCapitalEquityQuote: 'capital_equity_quote',
  MessageTypeCapitalEquityQuoteSuccess: 'capital_equity_quote_success',
  MessageTypeCapitalEquityQuoteFailed: 'capital_equity_quote_failed',
  MessageTypeCapitalEquityOrder: 'capital_equity_order',
  MessageTypeCapitalEquityOrderSuccess: 'capital_equity_order_success',
  MessageTypeCapitalEquityOrderFailed: 'capital_equity_order_failed',
  // DevTools message types
  MessageTypeDevSaveStressLocal: 'dev_save_stress_local',
  MessageTypeDevSaveStressLocalSuccess: 'dev_save_stress_local_success',
  MessageTypeDevSaveStressLocalFailed: 'dev_save_stress_local_failed',
  // Admin cheat message types
  MessageTypeAdminAddResources: 'admin_add_resources',
  MessageTypeAdminAddResourcesSuccess: 'admin_add_resources_success',
  MessageTypeAdminAddResourcesFailed: 'admin_add_resources_failed',
  MessageTypeAdminJumpToNextTick: 'admin_jump_to_next_tick',
  MessageTypeAdminJumpToNextTickSuccess: 'admin_jump_to_next_tick_success',
  MessageTypeAdminJumpToNextTickFailed: 'admin_jump_to_next_tick_failed',
  MessageTypeAdminSetInnovationSpendLocked: 'admin_set_innovation_spend_locked',
  MessageTypeAdminSetInnovationSpendLockedSuccess: 'admin_set_innovation_spend_locked_success',
  MessageTypeAdminSetInnovationSpendLockedFailed: 'admin_set_innovation_spend_locked_failed',
  MessageTypeAdminForceEndMatch: 'admin_force_end_match',
  MessageTypeAdminForceEndMatchSuccess: 'admin_force_end_match_success',
  MessageTypeAdminForceEndMatchFailed: 'admin_force_end_match_failed',
  // Policy toggle message types
  MessageTypeTogglePolicy: 'toggle_policy',
  MessageTypeTogglePolicySuccess: 'toggle_policy_success',
  MessageTypeTogglePolicyFailed: 'toggle_policy_failed',
  MessageTypePolicyStatesSync: 'policy_states_sync',
  // Emoji message types
  MessageTypeSendEmoji: 'send_emoji',
  MessageTypeEmojiReceived: 'emoji_received',
  // Ping message types
  MessageTypeSendPing: 'send_ping',
  MessageTypePingReceived: 'ping_received',
  // Route share message types
  MessageTypeRequestRouteShare: 'request_route_share',
  MessageTypeRouteShareReport: 'route_share_report',
  MessageTypeRouteShareFailed: 'route_share_failed',
  // Help request message types
  MessageTypeRequestHelp: 'request_help',
  MessageTypeRequestHelpSuccess: 'request_help_success',
  MessageTypeRequestHelpFailed: 'request_help_failed',
  MessageTypeCancelHelp: 'cancel_help',
  MessageTypeCancelHelpSuccess: 'cancel_help_success',
  MessageTypeHelpRequestResolved: 'help_request_resolved',
  // Section presence message types
  MessageTypeSectionPresenceState: 'section_presence_state',
  MessageTypeSectionPresenceUpdate: 'section_presence_update',
  MessageTypeSectionCursorPosition: 'section_cursor_position',
  // Co-sign message types
  MessageTypeButtonHover: 'button_hover',
  MessageTypeButtonHoverUpdate: 'button_hover_update',
  MessageTypeButtonHoverState: 'button_hover_state',
  // Budget message types
  MessageTypeBudgetState: 'budget_state',
  MessageTypeBudgetUpdate: 'budget_update',
  MessageTypeBudgetRefresh: 'budget_refresh',
  // Tick processing message types
  MessageTypeTickProcessingState: 'tick_processing_state',
  // City access message types
  MessageTypeUnlockCity: 'unlock_city',
  MessageTypeUnlockCitySuccess: 'unlock_city_success',
  MessageTypeUnlockCityFailed: 'unlock_city_failed',
  MessageTypeUnlockedCitiesState: 'unlocked_cities_state',
  // Commercial (the city's Market tab) message types
  MessageTypeCommercialAction: 'commercial_action',
  MessageTypeCommercialState: 'commercial_state',
  MessageTypeCommercialActionFailed: 'commercial_action_failed',
  MessageTypeInfluenceState: 'influence_state',
  // Performance monitoring message types
  MessageTypeWorldSaveStart: 'world_save_start',
  MessageTypeWorldSaveEnd: 'world_save_end',
  MessageTypeWorldSavePrep: 'world_save_prep',
  MessageTypeManifestTickStart: 'manifest_tick_start',
  MessageTypeManifestTickEnd: 'manifest_tick_end',
  // Phase change message type
  MessageTypePhaseChange: 'phase_change',
  // Session script step, voice chat control, dialogue, and team activity message types
  MessageTypeSessionScriptStep: 'session_script_step',
  MessageTypeVoiceChatControl: 'voice_chat_control',
  MessageTypeDialogueComplete: 'dialogue_complete',
  MessageTypeGlobalAudioMute: 'global_audio_mute',
  MessageTypeTeamActivity: 'team_activity',
  MessageTypeTeamActivityAction: 'team_activity_action',
  MessageTypeAchievementAwarded: 'achievement_awarded',
} as const;

export type MessageType = typeof MessageType[keyof typeof MessageType];

// ===== NARROWING HELPERS =====
// Incoming socket payloads are JSON-decoded into plain objects and arrive as
// `unknown`. These helpers let the type guards below narrow without casts.

/** Narrow an unknown value to a plain object record. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Narrow an unknown payload to an object whose `type` discriminator matches.
 * Every guard below starts with this check; field checks then refine further.
 */
function isMessageOfType(data: unknown, type: MessageType): data is Record<string, unknown> {
  return isRecord(data) && data.type === type;
}

// Type guards for message validation
export function isPlayerJoinedMessage(data: unknown): data is { type: typeof MessageType.MessageTypePlayerJoined; online_player_data: OnlinePlayerData } {
  return isMessageOfType(data, MessageType.MessageTypePlayerJoined) && data.online_player_data !== undefined;
}

export function isPlayersListMessage(data: unknown): data is { type: typeof MessageType.MessageTypePlayersList; online_players_data: Record<string, OnlinePlayerData>; teams?: Record<string, Team> } {
  return isMessageOfType(data, MessageType.MessageTypePlayersList) && data.online_players_data !== undefined;
}

export function isPlayerLeftMessage(data: unknown): data is { type: typeof MessageType.MessageTypePlayerLeft; user_id: string } {
  return isMessageOfType(data, MessageType.MessageTypePlayerLeft) && typeof data.user_id === 'string';
}

export function isPlayerDisconnectedMessage(data: unknown): data is { type: typeof MessageType.MessageTypePlayerDisconnected; user_id: string } {
  return isMessageOfType(data, MessageType.MessageTypePlayerDisconnected) && typeof data.user_id === 'string';
}

export function isLiveKitTokenMessage(data: unknown): data is { type: typeof MessageType.MessageTypeLiveKitToken; server_url: string; participant_token: string; room_name: string } {
  return (
    isMessageOfType(data, MessageType.MessageTypeLiveKitToken) &&
    typeof data.server_url === 'string' &&
    typeof data.participant_token === 'string' &&
    typeof data.room_name === 'string'
  );
}

export function isAirportsStateMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAirportsState; airports: Record<number, Airport> } {
  return isMessageOfType(data, MessageType.MessageTypeAirportsState) && data.airports !== undefined;
}

// ===== FLEET TYPE GUARDS =====

export function isFleetsStateMessage(data: unknown): data is { type: typeof MessageType.MessageTypeFleetsState; fleets: Record<string, Fleet> } {
  return isMessageOfType(data, MessageType.MessageTypeFleetsState);
}

export function isAvailablePlanesStateMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAvailablePlanesState; cohort_id: string; available_planes: AvailablePlanes } {
  return isMessageOfType(data, MessageType.MessageTypeAvailablePlanesState) && typeof data.cohort_id === 'string';
}

export function isAirplaneModelsStateMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAirplaneModelsState; airplane_models: AirplaneModel[] } {
  return isMessageOfType(data, MessageType.MessageTypeAirplaneModelsState) && Array.isArray(data.airplane_models);
}

export function isAddFleetSlotSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAddFleetSlotSuccess; fleet: Fleet; cohort_id: string; sender_user_id?: string } {
  return isMessageOfType(data, MessageType.MessageTypeAddFleetSlotSuccess);
}

export function isAddFleetSlotFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAddFleetSlotFailed } & FleetRefusal {
  return isMessageOfType(data, MessageType.MessageTypeAddFleetSlotFailed) && typeof data.error === 'string';
}

export function isRemoveFleetSlotSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeRemoveFleetSlotSuccess; fleet: Fleet; cohort_id: string; sender_user_id?: string } {
  return isMessageOfType(data, MessageType.MessageTypeRemoveFleetSlotSuccess);
}

export function isRemoveFleetSlotFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeRemoveFleetSlotFailed } & FleetRefusal {
  return isMessageOfType(data, MessageType.MessageTypeRemoveFleetSlotFailed) && typeof data.error === 'string';
}

export function isUpdateFleetSlotSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeUpdateFleetSlotSuccess; fleet: Fleet; cohort_id: string; sender_user_id?: string } {
  return isMessageOfType(data, MessageType.MessageTypeUpdateFleetSlotSuccess);
}

export function isUpdateFleetSlotFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeUpdateFleetSlotFailed } & FleetRefusal {
  return isMessageOfType(data, MessageType.MessageTypeUpdateFleetSlotFailed) && typeof data.error === 'string';
}

export function isPurchaseAirplanesSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypePurchaseAirplanesSuccess; fleet: Fleet; team: Team; cohort_id: string; sender_user_id?: string; slot_index: number; model_number: number; quantity: number } {
  return isMessageOfType(data, MessageType.MessageTypePurchaseAirplanesSuccess);
}

export function isPurchaseAirplanesFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypePurchaseAirplanesFailed } & OrderRefusal {
  return isMessageOfType(data, MessageType.MessageTypePurchaseAirplanesFailed) && typeof data.error === 'string';
}

export function isSellAirplanesSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeSellAirplanesSuccess; fleet: Fleet; team: Team; cohort_id: string; proceeds: number; sender_user_id?: string; slot_index: number; model_number: number; quantity: number } {
  return isMessageOfType(data, MessageType.MessageTypeSellAirplanesSuccess);
}

export function isSellAirplanesFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeSellAirplanesFailed } & OrderRefusal {
  return isMessageOfType(data, MessageType.MessageTypeSellAirplanesFailed) && typeof data.error === 'string';
}

// ===== QUERY MAX FLIGHTS TYPE GUARDS =====

interface QueryMaxFlightsResult {
  type: typeof MessageType.MessageTypeQueryMaxFlightsResult;
  max_flights_per_day: number;
  flight_time: number;
  origin_airport_id: number;
  dest_airport_id: number;
  fleet_slot_index: number;
}

export function isQueryMaxFlightsResultMessage(data: unknown): data is QueryMaxFlightsResult {
  return isMessageOfType(data, MessageType.MessageTypeQueryMaxFlightsResult) && typeof data.max_flights_per_day === 'number';
}

// ===== SEGMENT TYPE GUARDS =====

export function isSegmentsStateMessage(data: unknown): data is { type: typeof MessageType.MessageTypeSegmentsState; segments: Record<number, Segment>; by_city_pair: Record<number, number[]> } {
  return isMessageOfType(data, MessageType.MessageTypeSegmentsState);
}

export function isAddSegmentSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAddSegmentSuccess; segment: Segment; fleet: Fleet; cohort_id: string; sender_user_id?: string } {
  return isMessageOfType(data, MessageType.MessageTypeAddSegmentSuccess);
}

export function isAddSegmentFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAddSegmentFailed } & SegmentRefusal {
  return isMessageOfType(data, MessageType.MessageTypeAddSegmentFailed) && typeof data.error === 'string';
}

export function isUpdateFlightFrequencySuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeUpdateFlightFrequencySuccess; segment: Segment; cohort_id: string } {
  return isMessageOfType(data, MessageType.MessageTypeUpdateFlightFrequencySuccess);
}

export function isUpdateFlightFrequencyFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeUpdateFlightFrequencyFailed } & SegmentRefusal {
  return isMessageOfType(data, MessageType.MessageTypeUpdateFlightFrequencyFailed) && typeof data.error === 'string';
}

export function isRemoveFlightSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeRemoveFlightSuccess; segment_id: number; flight_id: number; fleet: Fleet; cohort_id: string; segment_removed?: boolean; sender_user_id?: string } {
  return isMessageOfType(data, MessageType.MessageTypeRemoveFlightSuccess);
}

export function isRemoveFlightFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeRemoveFlightFailed } & SegmentRefusal {
  return isMessageOfType(data, MessageType.MessageTypeRemoveFlightFailed) && typeof data.error === 'string';
}

// ===== ITINERARY TYPE GUARDS =====

export function isUpdateItineraryPriceSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeUpdateItineraryPriceSuccess; itinerary: Itinerary } {
  return isMessageOfType(data, MessageType.MessageTypeUpdateItineraryPriceSuccess);
}

export function isUpdateItineraryPriceFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeUpdateItineraryPriceFailed; error: string } {
  return isMessageOfType(data, MessageType.MessageTypeUpdateItineraryPriceFailed) && typeof data.error === 'string';
}

export function isUpdateRoutePricingPolicySuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeUpdateRoutePricingPolicySuccess; route_policy: RoutePricingPolicyView; itineraries: Itinerary[] } {
  return isMessageOfType(data, MessageType.MessageTypeUpdateRoutePricingPolicySuccess);
}

export function isUpdateRoutePricingPolicyFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeUpdateRoutePricingPolicyFailed; error: string } {
  return isMessageOfType(data, MessageType.MessageTypeUpdateRoutePricingPolicyFailed) && typeof data.error === 'string';
}

export function isResetItineraryPriceToAutoSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeResetItineraryPriceToAutoSuccess; itinerary: Itinerary } {
  return isMessageOfType(data, MessageType.MessageTypeResetItineraryPriceToAutoSuccess);
}

export function isResetItineraryPriceToAutoFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeResetItineraryPriceToAutoFailed; error: string } {
  return isMessageOfType(data, MessageType.MessageTypeResetItineraryPriceToAutoFailed) && typeof data.error === 'string';
}

// ===== AIRPORT FLIGHTS TYPE GUARDS =====

export function isAirportFlightsStateMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAirportFlightsState; airport_id: number; itineraries: Itinerary[]; route_policies?: RoutePricingPolicyView[] } {
  return isMessageOfType(data, MessageType.MessageTypeAirportFlightsState);
}

export function isAirportFlightUpdateMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAirportFlightUpdate; itinerary: Itinerary } {
  return isMessageOfType(data, MessageType.MessageTypeAirportFlightUpdate);
}

export function isAirportFlightRemovedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAirportFlightRemoved; itinerary_id: number; airport_id: number } {
  return isMessageOfType(data, MessageType.MessageTypeAirportFlightRemoved);
}

// ===== TEAM SEGMENTS SUBSCRIPTION TYPE GUARDS =====

export function isTeamSegmentsStateMessage(data: unknown): data is { type: typeof MessageType.MessageTypeTeamSegmentsState; team_id: number; segments: Segment[] } {
  return isMessageOfType(data, MessageType.MessageTypeTeamSegmentsState);
}

export function isTeamSegmentsUpdateMessage(data: unknown): data is { type: typeof MessageType.MessageTypeTeamSegmentsUpdate; team_id: number; segments: Segment[]; partial?: boolean } {
  return isMessageOfType(data, MessageType.MessageTypeTeamSegmentsUpdate);
}

// ===== FINANCIAL TYPE GUARDS =====

export interface FinancialReport {
  team_id: number;
  spending: number;
  revenue: number;
  cargo_revenue: number;
  profit: number;
  tick: number;
  km_flown: number;
  passengers: number;
  passenger_km: number;
  influence_earned: number;
}

export interface QuarterlyReport {
  team_id: number;
  quarter_id: number;
  revenue: number;
  cargo_revenue: number;
  spending: number;
  profit: number;
  interest_expense: number;
  depreciation_expense: number;
  other_income_expense: number;
  passengers: number;
  passenger_km: number;
  operating_cash_flow: number;
  investing_cash_flow: number;
  aircraft_book_value: number;
  rival_equity: number;
  city_access_value: number;
  building_value: number;
  cash_start: number;
  cash_end: number;
  ticks_with_entries: number;
  ticks_in_quarter: number;
  complete: boolean;
  // Operating expense breakdown
  fuel_cost: number;
  landing_fees: number;
  crew_cost: number;
  maintenance_cost: number;
  ground_cost: number;
  service_cost: number;
  building_cost: number;
  congestion_cost: number;
  research_cost: number;
  network_overhead_cost: number;
  // Derived efficiency and financial metrics
  available_seat_km: number;
  rask: number; // Revenue per Available Seat-Kilometer (cents)
  cask: number; // Cost per Available Seat-Kilometer (cents)
  roa: number; // Return on Assets
  roe: number; // Return on Equity
  debt_to_equity: number;
}

export interface AnnualReport {
  team_id: number;
  year: number;
  quarter_start: number;
  quarter_end: number;
  revenue: number;
  cargo_revenue: number;
  spending: number;
  profit: number;
  interest_expense: number;
  depreciation_expense: number;
  other_income_expense: number;
  passengers: number;
  passenger_km: number;
  operating_cash_flow: number;
  investing_cash_flow: number;
  aircraft_book_value: number;
  rival_equity: number;
  city_access_value: number;
  building_value: number;
  cash_start: number;
  cash_end: number;
  quarters_with_entries: number;
  quarters_in_year: number;
  complete: boolean;
  // Operating expense breakdown
  fuel_cost: number;
  landing_fees: number;
  crew_cost: number;
  maintenance_cost: number;
  ground_cost: number;
  service_cost: number;
  building_cost: number;
  congestion_cost: number;
  research_cost: number;
  network_overhead_cost: number;
  // Derived efficiency and financial metrics
  available_seat_km: number;
  rask: number; // Revenue per Available Seat-Kilometer (cents)
  cask: number; // Cost per Available Seat-Kilometer (cents)
  roa: number; // Return on Assets
  roe: number; // Return on Equity
  debt_to_equity: number;
}

export interface IncomeStatement {
  team_id: number;
  quarter_id: number;
  revenue: number;
  cargo_revenue: number;
  spending: number;
  profit: number;
  operating_margin: number;
  // Operating expense breakdown (sub-categories of spending)
  fuel_cost: number;
  crew_cost: number;
  maintenance_cost: number;
  ground_cost: number;
  landing_fees: number;
  service_cost: number;
  building_cost: number;
  congestion_cost: number;
  research_cost: number;
  network_overhead_cost: number;
  interest_expense: number;
  depreciation_expense: number;
  other_income_expense: number;
  net_income: number;
  net_profit_margin: number;
  complete: boolean;
}

interface BalanceSheet {
  team_id: number;
  quarter_id: number;
  cash: number;
  aircraft: number;
  rival_equity: number;
  city_access: number;
  buildings: number;
  debt: number;
  equity: number;
  complete: boolean;
}

interface CashFlowStatement {
  team_id: number;
  quarter_id: number;
  cash_start: number;
  operating_cash_flow: number;
  investing_cash_flow: number;
  financing_cash_flow: number;
  net_change: number;
  cash_end: number;
  complete: boolean;
}

export interface FinancialStatements {
  team_id: number;
  quarter_id: number;
  income: IncomeStatement;
  balance: BalanceSheet;
  cash_flow: CashFlowStatement;
  complete: boolean;
}

export interface TTMMetrics {
  team_id: number;
  quarter_id: number;
  quarters_count: number;
  net_income: number; // Sum of net income over TTM period
  average_assets: number; // Average total assets over TTM period
  current_equity: number; // Current quarter equity
  roa: number; // TTM Net Income / Average Assets
  roe: number; // TTM Net Income / Current Equity
  complete: boolean; // True if full 4 quarters available
}

export interface StrengthReport {
  team_id: number;
  as_of_quarter: number;
  trailing_revenue: number;
  strength: number;
  quarters_with_entries: number;
  window_quarters: number;
  complete: boolean;
}

export function isFinancialReportMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeFinancialReport;
  reports: { [team_id: number]: FinancialReport };
  teams: { [team_id: number]: { team_id: number; new_cash: number; new_knowledge_points: number; new_influence: number; current_tick?: number; is_quarter_boundary?: boolean } };
  next_report_at: number;
  interval_seconds: number;
  session_ticks?: number;
} {
  return isMessageOfType(data, MessageType.MessageTypeFinancialReport) && 
         data.reports !== undefined && 
         data.next_report_at !== undefined;
}

export function isQuarterlyReportMessage(data: unknown): data is QuarterlyReportMessage {
  return isMessageOfType(data, MessageType.MessageTypeQuarterlyReport) &&
         data.quarter_id !== undefined &&
         data.reports !== undefined;
}

export function isAnnualReportMessage(data: unknown): data is AnnualReportMessage {
  return isMessageOfType(data, MessageType.MessageTypeAnnualReport) &&
         data.year !== undefined &&
         data.reports !== undefined;
}

export function isCFOStatementsMessage(data: unknown): data is CFOStatementsMessage {
  return isMessageOfType(data, MessageType.MessageTypeCFOStatements) &&
         data.quarter_id !== undefined &&
         data.statements !== undefined;
}

/**
 * The reports' history a join is sent (OpCodeReportsHistory): report messages,
 * each with the op code it goes under live and its payload as it goes live,
 * in the order they ran. Each payload is held to its own guard as it is routed.
 */
export interface ReportsHistoryMessage {
  type: typeof MessageType.MessageTypeReportsHistory;
  messages: { op_code: number; payload: unknown }[];
}

export function isReportsHistoryMessage(data: unknown): data is ReportsHistoryMessage {
  return (
    isMessageOfType(data, MessageType.MessageTypeReportsHistory) &&
    Array.isArray(data.messages) &&
    data.messages.every((message) => isRecord(message) && typeof message.op_code === 'number' && isRecord(message.payload))
  );
}

export function isTTMMetricsMessage(data: unknown): data is TTMMetricsMessage {
  return isMessageOfType(data, MessageType.MessageTypeTTMMetrics) &&
         data.quarter_id !== undefined &&
         data.metrics !== undefined;
}

export function isStrengthReportMessage(data: unknown): data is StrengthReportMessage {
  return isMessageOfType(data, MessageType.MessageTypeStrengthReport) &&
         data.quarter_id !== undefined &&
         data.reports !== undefined;
}

export function isCapitalStateMessage(data: unknown): data is CapitalStateMessage {
  return isMessageOfType(data, MessageType.MessageTypeCapitalState) &&
         data.capital !== undefined;
}

export function isTimerSyncMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeTimerSync;
  next_report_at: number;
  interval_seconds: number;
  current_team_tick?: number;
  session_ticks?: number;
  innovation_spend_locked?: boolean;
  is_tick_processing?: boolean;
  tick_processing_started_at_unix?: number;
} {
  return isMessageOfType(data, MessageType.MessageTypeTimerSync) && typeof data.next_report_at === 'number' && typeof data.interval_seconds === 'number';
}

export function isTickProcessingStateMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeTickProcessingState;
  is_processing: boolean;
  started_at_unix: number;
  duration_ms?: number;
  game_tick?: number;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeTickProcessingState) &&
    typeof data.is_processing === 'boolean' &&
    typeof data.started_at_unix === 'number' &&
    (data.duration_ms === undefined || typeof data.duration_ms === 'number') &&
    (data.game_tick === undefined || typeof data.game_tick === 'number')
  );
}

// ===== CAPITAL TYPE GUARDS =====

export function isCapitalDebtQuoteSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeCapitalDebtQuoteSuccess; team_id: number; quote: DebtQuote } {
  return isMessageOfType(data, MessageType.MessageTypeCapitalDebtQuoteSuccess) && data.quote !== undefined;
}

export function isCapitalDebtQuoteFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeCapitalDebtQuoteFailed } & OrderRefusal {
  return isMessageOfType(data, MessageType.MessageTypeCapitalDebtQuoteFailed) && typeof data.error === 'string';
}

export function isCapitalDebtOrderSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeCapitalDebtOrderSuccess; execution: DebtExecution; capital: TeamCapital; team: Team } {
  return isMessageOfType(data, MessageType.MessageTypeCapitalDebtOrderSuccess) && data.execution !== undefined;
}

export function isCapitalDebtOrderFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeCapitalDebtOrderFailed } & OrderRefusal {
  return isMessageOfType(data, MessageType.MessageTypeCapitalDebtOrderFailed) && typeof data.error === 'string';
}

export function isCapitalEquityQuoteSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeCapitalEquityQuoteSuccess; team_id: number; quote: EquityQuote } {
  return isMessageOfType(data, MessageType.MessageTypeCapitalEquityQuoteSuccess) && data.quote !== undefined;
}

export function isCapitalEquityQuoteFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeCapitalEquityQuoteFailed } & OrderRefusal {
  return isMessageOfType(data, MessageType.MessageTypeCapitalEquityQuoteFailed) && typeof data.error === 'string';
}

export function isCapitalEquityOrderSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeCapitalEquityOrderSuccess; execution: EquityExecution; capital: TeamCapital; team: Team } {
  return isMessageOfType(data, MessageType.MessageTypeCapitalEquityOrderSuccess) && data.execution !== undefined;
}

export function isCapitalEquityOrderFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeCapitalEquityOrderFailed } & OrderRefusal {
  return isMessageOfType(data, MessageType.MessageTypeCapitalEquityOrderFailed) && typeof data.error === 'string';
}

// ===== INNOVATION TYPE GUARDS =====

interface UnlockInnovationSuccessMessage {
  type: typeof MessageType.MessageTypeUnlockInnovationSuccess;
  innovation_id: string;
  team: Team;
  cohort_id: string;
  fleet?: Fleet; // Updated fleet data (plane innovations upgrade fleet slots, fleet innovations may modify seats)
  segments?: Segment[]; // Updated segments (plane/fleet innovations update segment stats)
  airplane_models?: AirplaneModel[]; // Updated airplane models (fleet innovations modify model specs)
  /** Per-player KP deductions from the copay split. Used to update each contributor's
   * balance in the players store without waiting for a full PlayersList broadcast. */
  copay?: { user_id: string; amount_paid: number; new_knowledge_points: number }[];
}

export function isUnlockInnovationSuccessMessage(data: unknown): data is UnlockInnovationSuccessMessage {
  return isMessageOfType(data, MessageType.MessageTypeUnlockInnovationSuccess) && typeof data.innovation_id === 'string';
}

export function isUnlockInnovationFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeUnlockInnovationFailed } & OrderRefusal {
  return isMessageOfType(data, MessageType.MessageTypeUnlockInnovationFailed) && typeof data.error === 'string';
}

// ===== DEVTOOLS TYPE GUARDS =====

export function isDevSaveStressLocalSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeDevSaveStressLocalSuccess; save_id: string; message: string } {
  return isMessageOfType(data, MessageType.MessageTypeDevSaveStressLocalSuccess) && typeof data.save_id === 'string';
}

export function isDevSaveStressLocalFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeDevSaveStressLocalFailed; error: string } {
  return isMessageOfType(data, MessageType.MessageTypeDevSaveStressLocalFailed) && typeof data.error === 'string';
}

// ===== ADMIN CHEAT TYPE GUARDS =====

export function isAdminAddResourcesSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAdminAddResourcesSuccess; team: Team; cohort_id: string; sender_user_id?: string } {
  return isMessageOfType(data, MessageType.MessageTypeAdminAddResourcesSuccess) && data.team !== undefined;
}

export function isAdminAddResourcesFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAdminAddResourcesFailed; error: string } {
  return isMessageOfType(data, MessageType.MessageTypeAdminAddResourcesFailed) && typeof data.error === 'string';
}

export function isAdminJumpToNextTickSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAdminJumpToNextTickSuccess; message: string; sender_user_id?: string } {
  return isMessageOfType(data, MessageType.MessageTypeAdminJumpToNextTickSuccess) && typeof data.message === 'string';
}

export function isAdminJumpToNextTickFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAdminJumpToNextTickFailed; error: string } {
  return isMessageOfType(data, MessageType.MessageTypeAdminJumpToNextTickFailed) && typeof data.error === 'string';
}

export function isAdminSetInnovationSpendLockedSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAdminSetInnovationSpendLockedSuccess; innovation_spend_locked: boolean; sender_user_id?: string } {
  return isMessageOfType(data, MessageType.MessageTypeAdminSetInnovationSpendLockedSuccess) && typeof data.innovation_spend_locked === 'boolean';
}

export function isAdminSetInnovationSpendLockedFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAdminSetInnovationSpendLockedFailed; error: string } {
  return isMessageOfType(data, MessageType.MessageTypeAdminSetInnovationSpendLockedFailed) && typeof data.error === 'string';
}

export function isAdminForceEndMatchSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAdminForceEndMatchSuccess; message: string; sender_user_id?: string } {
  return isMessageOfType(data, MessageType.MessageTypeAdminForceEndMatchSuccess) && typeof data.message === 'string';
}

export function isAdminForceEndMatchFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeAdminForceEndMatchFailed; error: string } {
  return isMessageOfType(data, MessageType.MessageTypeAdminForceEndMatchFailed) && typeof data.error === 'string';
}

// ===== MISSION TYPE GUARDS =====

export function isMissionsStateMessage(data: unknown): data is { type: typeof MessageType.MessageTypeMissionsState; cohort_id: string; mission_store: TeamMissionStore } {
  return isMessageOfType(data, MessageType.MessageTypeMissionsState) && data.mission_store !== undefined;
}

export function isMissionProgressMessage(data: unknown): data is { type: typeof MessageType.MessageTypeMissionProgress; cohort_id: string; missions: Mission[] } {
  return isMessageOfType(data, MessageType.MessageTypeMissionProgress) && data.missions !== undefined;
}

export function isMissionCompletedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeMissionCompleted; cohort_id: string; mission: Mission; team: Team; mission_store: TeamMissionStore } {
  return isMessageOfType(data, MessageType.MessageTypeMissionCompleted) && data.mission !== undefined && data.mission_store !== undefined;
}

// ===== POLICY TOGGLE TYPE GUARDS =====

export function isPolicyStatesSyncMessage(data: unknown): data is { type: typeof MessageType.MessageTypePolicyStatesSync; cohort_id: string; policy_states: Record<string, boolean> } {
  return isMessageOfType(data, MessageType.MessageTypePolicyStatesSync) && typeof data.cohort_id === 'string';
}

export function isTogglePolicySuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeTogglePolicySuccess; sender_user_id?: string; cohort_id: string; innovation_id: string; enabled: boolean } {
  return isMessageOfType(data, MessageType.MessageTypeTogglePolicySuccess) && typeof data.innovation_id === 'string';
}

export function isTogglePolicyFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeTogglePolicyFailed } & OrderRefusal {
  return isMessageOfType(data, MessageType.MessageTypeTogglePolicyFailed) && typeof data.error === 'string';
}

// ===== EMOJI TYPE GUARDS =====

export function isEmojiReceivedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeEmojiReceived; user_id: string; emoji_id: number } {
  return isMessageOfType(data, MessageType.MessageTypeEmojiReceived) && typeof data.user_id === 'string' && typeof data.emoji_id === 'number';
}

// ===== PING TYPE GUARDS =====

export function isPingReceivedMessage(data: unknown): data is { type: typeof MessageType.MessageTypePingReceived; user_id: string; lat: number; lng: number } {
  return isMessageOfType(data, MessageType.MessageTypePingReceived) && typeof data.user_id === 'string' && typeof data.lat === 'number' && typeof data.lng === 'number';
}

// ===== PRIVATE CHAT TYPE GUARDS =====

export function isPrivateChatStartMessage(data: unknown): data is PrivateChatStartMessage {
  return (
    isMessageOfType(data, MessageType.MessageTypePrivateChatStart) &&
    typeof data.room_name === 'string' &&
    typeof data.moderator_name === 'string' &&
    typeof data.server_url === 'string' &&
    typeof data.participant_token === 'string'
  );
}

export function isPrivateChatEndMessage(data: unknown): data is PrivateChatEndMessage {
  return (
    isMessageOfType(data, MessageType.MessageTypePrivateChatEnd) &&
    typeof data.reason === 'string'
  );
}

// ===== SESSION'S END AND REMOVAL TYPE GUARDS =====

/**
 * The session's end (OpCodeSessionEnded): asks only for the world it was
 * played in, the one field its notice reads — one turned away for a field
 * nothing reads would leave the player in an ended session with no notice.
 */
export function isSessionEndedMessage(data: unknown): data is SessionEndedMessage {
  return isMessageOfType(data, MessageType.MessageTypeSessionEnded) && typeof data.world_name === 'string';
}

/**
 * A moderator's removal (OpCodePlayerBanned). Only when it ends is read, so
 * only that is required: a removal the guard turned away would leave the
 * player reconnecting into a refused join behind no notice at all.
 */
export function isPlayerBannedMessage(data: unknown): data is PlayerBannedMessage {
  return isMessageOfType(data, MessageType.MessageTypePlayerBanned) && typeof data.expires_at === 'number';
}

// ===== SECTION PRESENCE TYPE GUARDS =====

export function isSectionPresenceStateMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeSectionPresenceState;
  sections: Record<string, string>;
  city_focus?: Record<string, number>;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeSectionPresenceState) &&
    typeof data.sections === 'object' &&
    data.sections !== null
  );
}

export function isSectionPresenceUpdateMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeSectionPresenceUpdate;
  user_id: string;
  section: string;
  city_id?: number | null;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeSectionPresenceUpdate) &&
    typeof data.user_id === 'string' &&
    typeof data.section === 'string'
  );
}

// ===== SECTION CURSOR TYPE GUARDS =====

export function isSectionCursorPositionMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeSectionCursorPosition;
  user_id: string;
  x: number;
  y: number;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeSectionCursorPosition) &&
    typeof data.user_id === 'string' &&
    typeof data.x === 'number' &&
    typeof data.y === 'number'
  );
}

// ===== BUDGET TYPE GUARDS =====

export function isBudgetStateMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeBudgetState;
  budgets: Record<string, number>;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeBudgetState) &&
    typeof data.budgets === 'object' &&
    data.budgets !== null
  );
}

export function isBudgetUpdateMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeBudgetUpdate;
  budgets: Record<string, number>;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeBudgetUpdate) &&
    typeof data.budgets === 'object' &&
    data.budgets !== null
  );
}

export function isBudgetRefreshMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeBudgetRefresh;
  budgets: Record<string, number>;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeBudgetRefresh) &&
    typeof data.budgets === 'object' &&
    data.budgets !== null
  );
}

// ===== CO-SIGN TYPE GUARDS =====

export function isButtonHoverUpdateMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeButtonHoverUpdate;
  user_id: string;
  button_id: string;
  hovering: boolean;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeButtonHoverUpdate) &&
    typeof data.user_id === 'string' &&
    typeof data.button_id === 'string' &&
    typeof data.hovering === 'boolean'
  );
}

export function isButtonHoverStateMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeButtonHoverState;
  button_id: string;
  hoverers: string[];
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeButtonHoverState) &&
    typeof data.button_id === 'string' &&
    Array.isArray(data.hoverers)
  );
}

// ===== CITY ACCESS TYPE GUARDS =====

export function isUnlockCitySuccessMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeUnlockCitySuccess;
  city_id: number;
  team: Team;
  cohort_id: string;
  cost: number;
  sender_user_id: string;
  approvers: string[];
  airport?: Airport;
  global_hq_set?: boolean;
  unlock_source?: 'city_access' | 'step-2-hq-selection';
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeUnlockCitySuccess) &&
    typeof data.city_id === 'number' &&
    typeof data.cohort_id === 'string'
  );
}

export function isUnlockCityFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeUnlockCityFailed } & OrderRefusal {
  return (
    isMessageOfType(data, MessageType.MessageTypeUnlockCityFailed) &&
    typeof data.error === 'string'
  );
}

/**
 * The team's commercial state (OpCodeCommercialState): asks for the team it is
 * of, which its handler reads, and for what the city panel and the tree read
 * of it without asking first — the cycle, the cities, the Advisor, the
 * Wonders, the studies under way and the contracts. The server sends each,
 * every time (commercial-messages.go, BuildCommercialView).
 */
export function isCommercialStateMessage(data: unknown): data is CommercialSnapshot {
  return (
    isMessageOfType(data, MessageType.MessageTypeCommercialState) &&
    typeof data.cohort_id === 'string' &&
    typeof data.cycle === 'number' &&
    isRecord(data.cities) &&
    isRecord(data.advisor) &&
    isRecord(data.wonders) &&
    Array.isArray(data.pending) &&
    Array.isArray(data.contracts)
  );
}

/** Influence as it accrues (OpCodeInfluenceState): each team's figures, by cohort — every one of them read. */
export function isInfluenceStateMessage(data: unknown): data is InfluenceStateMessage {
  return (
    isMessageOfType(data, MessageType.MessageTypeInfluenceState) &&
    isRecord(data.teams) &&
    Object.values(data.teams).every(
      (team) =>
        isRecord(team) &&
        typeof team.influence === 'number' &&
        typeof team.influence_seconds === 'number' &&
        typeof team.accruing === 'boolean',
    )
  );
}

export function isCommercialActionFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeCommercialActionFailed } & OrderRefusal {
  return (
    isMessageOfType(data, MessageType.MessageTypeCommercialActionFailed) &&
    typeof data.error === 'string'
  );
}

export function isUnlockedCitiesStateMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeUnlockedCitiesState;
  cohort_id: string;
  unlocked_city_ids: number[];
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeUnlockedCitiesState) &&
    typeof data.cohort_id === 'string' &&
    Array.isArray(data.unlocked_city_ids)
  );
}

export function isWorldSaveStartMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeWorldSaveStart;
  started_at_unix: number;
  game_tick: number;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeWorldSaveStart) &&
    typeof data.started_at_unix === 'number' &&
    typeof data.game_tick === 'number'
  );
}

export function isWorldSavePrepMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeWorldSavePrep;
  phase: 'serializing' | 'serialized' | 'sent';
  started_at_unix: number;
  game_tick: number;
  serialization_ms?: number;
  data_size_bytes?: number;
  compression_ms?: number;
  send_ms?: number;
  total_prep_ms?: number;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeWorldSavePrep) &&
    typeof data.phase === 'string' &&
    typeof data.game_tick === 'number'
  );
}

export function isWorldSaveEndMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeWorldSaveEnd;
  started_at_unix: number;
  duration_ms: number;
  game_tick: number;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeWorldSaveEnd) &&
    typeof data.started_at_unix === 'number' &&
    typeof data.duration_ms === 'number' &&
    typeof data.game_tick === 'number'
  );
}

export function isManifestTickStartMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeManifestTickStart;
  started_at_unix: number;
  game_tick: number;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeManifestTickStart) &&
    typeof data.started_at_unix === 'number' &&
    typeof data.game_tick === 'number'
  );
}

export function isManifestTickEndMessage(data: unknown): data is {
  type: typeof MessageType.MessageTypeManifestTickEnd;
  started_at_unix: number;
  duration_ms: number;
  game_tick: number;
} {
  return (
    isMessageOfType(data, MessageType.MessageTypeManifestTickEnd) &&
    typeof data.started_at_unix === 'number' &&
    typeof data.duration_ms === 'number' &&
    typeof data.game_tick === 'number'
  );
}

// ===== PHASE CHANGE TYPE GUARDS =====

export interface PhaseChangePayload {
  type: typeof MessageType.MessageTypePhaseChange;
  phase: 'start_lobby' | 'playing' | 'end_lobby';
  scheduled_start_utc: number;
  scheduled_end_utc: number;
  end_lobby_end_utc: number;
}

export function isPhaseChangeMessage(data: unknown): data is PhaseChangePayload {
  return (
    isMessageOfType(data, MessageType.MessageTypePhaseChange) &&
    typeof data.phase === 'string' &&
    typeof data.scheduled_start_utc === 'number' &&
    typeof data.scheduled_end_utc === 'number'
  );
}

// ===== SESSION SCRIPT STEP TYPE GUARDS =====

export interface SessionScriptFeatureState {
  voiceChatEnabled: boolean;
  quarterProcessingEnabled: boolean;
  /** Section IDs that players cannot access during this step. Empty array = all unlocked. */
  lockedSections: string[];
  /** When false, all player actions that spend Cash are disabled. Defaults to true. */
  cashEnabled: boolean;
  /** When false, all player actions that spend Influence are disabled. Defaults to true. */
  influenceEnabled: boolean;
  /**
   * When false and missionOverrideText is empty, the mission indicator is hidden entirely.
   * Defaults to true (missions shown). Session script steps set this to false during guided phases
   * where missions are not yet actionable.
   */
  missionsVisible: boolean;
  /**
   * An i18n key for a temporary message shown in the mission indicator instead of the
   * current mission. When non-empty, takes precedence over missionsVisible and the actual
   * mission display. Cleared (empty string) when returning to normal mission display.
   */
  missionOverrideText?: string;
  /**
   * An i18n key for additional descriptive text shown in the mission indicator tooltip
   * when missionOverrideText is active. Optional.
   */
  missionOverrideDescription?: string;
}

/** Trigger definition as sent by the server. */
export interface StepTrigger {
  type: string;
  quarter?: number;
  seconds?: number;
  event?: string;
}

/** Summary of a remaining session script step, used by the client to estimate time left. */
export interface SessionScriptStepRemainder {
  id: string;
  startTrigger: StepTrigger;
  endTrigger: StepTrigger;
}

export interface SessionScriptStepPayload {
  type: typeof MessageType.MessageTypeSessionScriptStep;
  /** The session script ID (its steps' names are under the i18n key `sessionScripts.<sessionScriptId>.steps`). Null if no session script. */
  session_script_id: string | null;
  /** The step ID within the session script (matches i18n key `sessionScripts.<sessionScriptId>.steps.<stepId>`). */
  step_id: string | null;
  /** Index of the current step (0-based). -1 when the session script is complete. */
  step_index: number;
  /** Total number of steps in the session script. */
  step_count: number;
  /** Current step state: "pending", "active", or "completed". */
  step_state: 'pending' | 'active' | 'completed';
  /** Whether the session script has finished all its steps. */
  complete: boolean;
  /** Current feature state from the FeatureController. */
  features: SessionScriptFeatureState;
  /** UTC Unix timestamp when this step started (0 if pending). */
  step_started_at_utc: number;
  /** End trigger for the current step (used for countdown display). */
  end_trigger?: StepTrigger;
  /** All remaining steps (current + future) so the client can estimate time left. */
  remaining_steps?: SessionScriptStepRemainder[];
  /** Session tick counter (1-based). Used directly for session-relative quarter display. */
  session_ticks?: number;
  /**
   * Dialogue ID to trigger when this step becomes active or completes.
   * Present only in the broadcast that fires with the step transition — not
   * repeated on subsequent ticks. Client should call startDialogueById with
   * this value when received.
   */
  dialogue_id?: string;
}

export function isSessionScriptStepMessage(data: unknown): data is SessionScriptStepPayload {
  return (
    isMessageOfType(data, MessageType.MessageTypeSessionScriptStep) &&
    typeof data.step_index === 'number' &&
    typeof data.complete === 'boolean' &&
    data.features !== undefined
  );
}

// ===== VOICE CHAT CONTROL TYPE GUARDS =====

interface VoiceChatControlPayload {
  type: typeof MessageType.MessageTypeVoiceChatControl;
  /** Whether the server permits players to send/receive audio. */
  enabled: boolean;
}

export function isVoiceChatControlMessage(data: unknown): data is VoiceChatControlPayload {
  return (
    isMessageOfType(data, MessageType.MessageTypeVoiceChatControl) &&
    typeof data.enabled === 'boolean'
  );
}

// ===== GLOBAL AUDIO MUTE TYPE GUARDS =====

interface GlobalAudioMutePayload {
  type: typeof MessageType.MessageTypeGlobalAudioMute;
  /** Always true when sent — the session-level audio mute is permanent for the session. */
  muted: boolean;
}

export function isGlobalAudioMuteMessage(data: unknown): data is GlobalAudioMutePayload {
  return (
    isMessageOfType(data, MessageType.MessageTypeGlobalAudioMute) &&
    typeof data.muted === 'boolean'
  );
}

// ===== TEAM ACTIVITY TYPE GUARDS =====

export interface TeamActivityPayload {
  type: typeof MessageType.MessageTypeTeamActivity;
  activity_id: string;
  phase_index: number;
  phase_count: number;
  phase_type: string;
  phase_state: Record<string, unknown>;
  shared_data: Record<string, unknown>;
  complete: boolean;
}

export function isTeamActivityMessage(data: unknown): data is TeamActivityPayload {
  return (
    isMessageOfType(data, MessageType.MessageTypeTeamActivity) &&
    typeof data.activity_id === 'string' &&
    typeof data.phase_type === 'string'
  );
}

// ===== ACHIEVEMENT TYPE GUARDS =====

export interface AchievementAwardedPayload {
  type: typeof MessageType.MessageTypeAchievementAwarded;
  achievement_id: string;
  image_id: string;
  name_key: string;
  description_key: string;
}

export function isAchievementAwardedMessage(data: unknown): data is AchievementAwardedPayload {
  return (
    isMessageOfType(data, MessageType.MessageTypeAchievementAwarded) &&
    typeof data.achievement_id === 'string' &&
    typeof data.name_key === 'string'
  );
}

// ===== HELP REQUEST TYPE GUARDS =====

export function isRequestHelpSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeRequestHelpSuccess; help_type: string } {
  return isMessageOfType(data, MessageType.MessageTypeRequestHelpSuccess);
}

export function isRequestHelpFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeRequestHelpFailed; error: string } {
  return isMessageOfType(data, MessageType.MessageTypeRequestHelpFailed) && typeof data.error === 'string';
}

export function isCancelHelpSuccessMessage(data: unknown): data is { type: typeof MessageType.MessageTypeCancelHelpSuccess } {
  return isMessageOfType(data, MessageType.MessageTypeCancelHelpSuccess);
}

export function isHelpRequestResolvedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeHelpRequestResolved } {
  return isMessageOfType(data, MessageType.MessageTypeHelpRequestResolved);
}

// ===== ROUTE SHARE TYPE GUARDS =====

export function isRouteShareReportMessage(data: unknown): data is { type: typeof MessageType.MessageTypeRouteShareReport; report: RouteShareReport } {
  return isMessageOfType(data, MessageType.MessageTypeRouteShareReport) && data.report !== undefined;
}

export function isRouteShareFailedMessage(data: unknown): data is { type: typeof MessageType.MessageTypeRouteShareFailed; error: string } {
  return isMessageOfType(data, MessageType.MessageTypeRouteShareFailed) && typeof data.error === 'string';
}
